// Get color class based on score
export const getScoreColor = (score) => {
  if (score >= 90) return 'text-green-500';
  if (score >= 80) return 'text-blue-500';
  if (score >= 70) return 'text-yellow-500';
  if (score >= 60) return 'text-orange-500';
  return 'text-red-500';
};

// Get background color class based on score
export const getScoreBgColor = (score) => {
  if (score >= 90) return 'bg-green-500/10 border-green-500/30';
  if (score >= 80) return 'bg-blue-500/10 border-blue-500/30';
  if (score >= 70) return 'bg-yellow-500/10 border-yellow-500/30';
  if (score >= 60) return 'bg-orange-500/10 border-orange-500/30';
  return 'bg-red-500/10 border-red-500/30';
};

// Get grade badge color
export const getGradeColor = (grade) => {
  const colors = {
    'A': 'bg-green-500 text-white',
    'B': 'bg-blue-500 text-white',
    'C': 'bg-yellow-500 text-white',
    'D': 'bg-orange-500 text-white',
    'F': 'bg-red-500 text-white',
  };
  return colors[grade] || 'bg-gray-500 text-white';
};

// Format processing time
export const formatProcessingTime = (ms) => {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
};

// Format date
export const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// Validate URL
export const isValidUrl = (url) => {
  try {
    new URL(url.startsWith('http') ? url : `https://${url}`);
    return true;
  } catch {
    return false;
  }
};

// Format URL for display
export const formatUrl = (url) => {
  try {
    const urlObj = new URL(url);
    return urlObj.hostname + urlObj.pathname;
  } catch {
    return url;
  }
};

// Get analyzer icon name
export const getAnalyzerIcon = (analyzerName) => {
  const icons = {
    technicalFoundation: 'Code2',
    contentStructure: 'FileText',
    pageLevelEEAT: 'Award',
    queryMatch: 'Search',
    aiVisibility: 'Eye',
    robotsTxt: 'FileSearch',
    markdownFiles: 'FileCode',
  };
  return icons[analyzerName] || 'FileText';
};

// Get analyzer display name
export const getAnalyzerDisplayName = (analyzerName) => {
  const names = {
    technicalFoundation: 'Technical Foundation',
    contentStructure: 'Content Structure',
    pageLevelEEAT: 'Page-Level E-E-A-T',
    queryMatch: 'Query Match',
    aiVisibility: 'AI Visibility',
    robotsTxt: 'Robots.txt Analysis',
    markdownFiles: 'Optimized Markdown',
  };
  return names[analyzerName] || analyzerName;
};

// Get analyzer importance explanation (Why this matters)
export const getAnalyzerImportance = (analyzerKey) => {
  const importance = {
    technicalFoundation: "Builds your Golden Record. Ensures your site's technical receipts are perfectly organized so AI bots can verify and cite your expertise.",
    contentStructure: "Optimizes your Signal. Well-structured content with clear hierarchies ensures AI models can accurately extract and synthesize your facts.",
    pageLevelEEAT: "Proves your Expertise. Demonstrating real-world experience and authority is the primary way to win high-confidence citations from AI engines.",
    queryMatch: "Aligns your Spotlight. Measures how precisely your content answers the specific questions users are asking AI assistants.",
    aiVisibility: "Maps your Presence. Monitors how 'visible' and accessible your content is specifically to AI-agent crawlers vs standard search engines.",
    robotsTxt: "The Front Door. If AI bots are blocked here, they will never see your Source, resulting in zero AI visibility.",
    markdownFiles: "The AI Shortcut. Machine-readable summaries provide the high-density facts that AI assistants love to cite.",
  };
  return importance[analyzerKey] || "";
};

// Get group display name
export const getGroupDisplayName = (groupId) => {
  const names = {
    discovery: "Discovery Pillar",
    clarity: "Clarity Pillar",
    trust: "Trust Pillar",
    relevance: "Relevance Pillar",
    'aeo-foundation': "Discovery Pillar", // Backward compatibility mapping
    'content-optimization': "Clarity Pillar",
    'authority': "Trust Pillar",
    'keyword-performance': "Relevance Pillar"
  };
  return names[groupId] || groupId.charAt(0).toUpperCase() + groupId.slice(1);
};

// Get group description
export const getGroupDescription = (groupId) => {
  const descriptions = {
    discovery: "Can AI bots find your Source?",
    clarity: "Can AI bots understand your Source?",
    trust: "Do AI bots believe your Source?",
    relevance: "Are you answering the right questions?",
    'aeo-foundation': "Can AI bots find your Source?",
    'content-optimization': "Can AI bots understand your Source?",
    'authority': "Do AI bots believe your Source?",
    'keyword-performance': "Are you answering the right questions?"
  };
  return descriptions[groupId] || "";
};

// Calculate percentage
export const calculatePercentage = (value, total) => {
  if (total === 0) return 0;
  return Math.round((value / total) * 100);
};

// Truncate text
export const truncateText = (text, maxLength = 100) => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

// Get recommendation priority icon
export const getRecommendationPriorityIcon = (text) => {
  if (text.toUpperCase().includes('CRITICAL')) return 'AlertTriangle';
  if (text.toUpperCase().includes('HIGH PRIORITY')) return 'AlertCircle';
  return 'Info';
};

// Get recommendation priority color
export const getRecommendationPriorityColor = (text) => {
  if (text.toUpperCase().includes('CRITICAL')) return 'text-red-500';
  if (text.toUpperCase().includes('HIGH PRIORITY')) return 'text-orange-500';
  return 'text-blue-500';
};

// Format finding values for UI display
export const formatFindingValue = (value) => {
  if (value === null || value === undefined) return 'None';
  if (typeof value === 'boolean') return value ? '✓' : '✗';
  
  if (typeof value === 'object' && value !== null) {
    // Handle special status objects (e.g. AI Context File)
    if ('present' in value) {
      return value.present ? 'Detected (15pt Bonus)' : 'Not Found';
    }

    // Handle heading count objects
    if (value.h1 !== undefined || value.h2 !== undefined || value.h3 !== undefined) {
      const parts = [];
      if (value.h1 !== undefined) parts.push(`H1: ${value.h1}`);
      if (value.h2 !== undefined) parts.push(`H2: ${value.h2}`);
      if (value.h3 !== undefined) parts.push(`H3: ${value.h3}`);
      return parts.length > 0 ? parts.join(', ') : 'None';
    }

    // Handle arrays
    if (Array.isArray(value)) {
      if (value.length === 0) return 'None';
      if (typeof value[0] === 'object') return `${value.length} items`;
      return value.join(', ');
    }

    // Fallback for other objects
    const keys = Object.keys(value);
    if (keys.length === 0) return 'None';
    return `${keys.length} items`;
  }
  
  return String(value);
};
